import { useState, useMemo, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { CalendarPlus, Eye, ListChecks } from '@phosphor-icons/react';
import { formatLocalDateString, HEBREW_DAYS_SHORT, HEBREW_DAYS_FULL } from '../../utils/dateUtils';
import { getHolidayForDate } from '../../utils/israeliHolidays';
import { useCalendarColorMode, resolveAppointmentColor, hexToRgba, darkenHex } from '../../utils/appointmentDisplay';
import './MonthView.css';

function MonthView({
  date,
  appointments,
  availabilitySlots = [],
  onDayClick,
  onAppointmentClick,
  onAppointmentContextMenu,
  onCreateAppointment,
  onAddTask,
  showTherapistName = false,
  therapistColorMap = null,
}) {
  const [contextMenu, setContextMenu] = useState(null);
  const contextMenuRef = useRef(null);
  // Clinic color mode. NOTE: the local getStatusColor() below is intentionally
  // NOT unified with AppointmentCard (it differs slightly) — status mode must
  // stay pixel-identical; only type mode branches off here.
  const { mode: colorMode, treatmentTypeMap } = useCalendarColorMode();

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  useEffect(() => {
    if (!contextMenu) return;
    const handleClick = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        closeContextMenu();
      }
    };
    const handleKey = (e) => { if (e.key === 'Escape') closeContextMenu(); };

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    document.addEventListener('scroll', closeContextMenu, true);

    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('scroll', closeContextMenu, true);
    };
  }, [contextMenu, closeContextMenu]);

  useLayoutEffect(() => {
    if (!contextMenu || !contextMenuRef.current) return;
    const rect = contextMenuRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let x = contextMenu.x;
    let y = contextMenu.y;
    if (x + rect.width > vw - 8) x = vw - rect.width - 8;
    if (y + rect.height > vh - 8) y = vh - rect.height - 8;
    contextMenuRef.current.style.left = `${x}px`;
    contextMenuRef.current.style.top = `${y}px`;
  }, [contextMenu]);

  const handleContextMenu = (e, dayDate) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, date: dayDate });
  };

  const handleMenuAction = (action) => {
    const selectedDate = contextMenu?.date;
    closeContextMenu();
    if (!selectedDate) return;
    if (action === 'create-appointment') onCreateAppointment(selectedDate);
    else if (action === 'view-day') onDayClick(selectedDate);
    else if (action === 'add-task') onAddTask(selectedDate);
  };
  // Generate calendar grid for the month
  const calendarDays = useMemo(() => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    // First day of the month
    const firstDay = new Date(year, month, 1);
    const startingDayOfWeek = firstDay.getDay();
    
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();
    
    // Previous month days to show
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    
    const days = [];
    
    // Previous month days
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonthLastDay - i),
        isCurrentMonth: false,
      });
    }
    
    // Current month days
    for (let day = 1; day <= totalDays; day++) {
      days.push({
        date: new Date(year, month, day),
        isCurrentMonth: true,
      });
    }
    
    // Next month days to complete the grid (6 rows)
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        date: new Date(year, month + 1, day),
        isCurrentMonth: false,
      });
    }
    
    return days;
  }, [date]);

  // Build availability lookup by date string
  const availabilityByDate = useMemo(() => {
    const result = {};
    availabilitySlots.forEach((slot) => {
      if (slot.slots && slot.slots.length > 0) {
        result[slot.date] = true;
      }
    });
    return result;
  }, [availabilitySlots]);

  // Build blocked-time lookup by date string
  const blockedByDate = useMemo(() => {
    const result = {};
    availabilitySlots.forEach((slot) => {
      if (slot.blocked && slot.blocked.length > 0) {
        result[slot.date] = true;
      }
    });
    return result;
  }, [availabilitySlots]);

  // Get appointments for each day
  const appointmentsByDay = useMemo(() => {
    const result = {};
    appointments.forEach((apt) => {
      const aptDate = new Date(apt.startTime);
      const key = `${aptDate.getFullYear()}-${aptDate.getMonth()}-${aptDate.getDate()}`;
      if (!result[key]) {
        result[key] = [];
      }
      result[key].push(apt);
    });
    return result;
  }, [appointments]);

  const isToday = (checkDate) => {
    const today = new Date();
    return (
      checkDate.getDate() === today.getDate() &&
      checkDate.getMonth() === today.getMonth() &&
      checkDate.getFullYear() === today.getFullYear()
    );
  };

  const getDayKey = (checkDate) => {
    return `${checkDate.getFullYear()}-${checkDate.getMonth()}-${checkDate.getDate()}`;
  };

  const getDateStr = (checkDate) => {
    // Use local date string to avoid timezone issues (Israel timezone)
    return formatLocalDateString(checkDate);
  };

  const hasAvailability = (checkDate) => {
    return availabilityByDate[getDateStr(checkDate)] || false;
  };

  const hasBlockedTime = (checkDate) => {
    return blockedByDate[getDateStr(checkDate)] || false;
  };

  // Format tooltip for a day
  const getDayTooltip = (checkDate) => {
    const dayName = HEBREW_DAYS_FULL[checkDate.getDay()];
    const dateFormatted = checkDate.toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric' });
    return `יום ${dayName}, ${dateFormatted}`;
  };

  const getStatusColor = (appointment) => {
    // Blocked time — must be first, blocks never progress through the lifecycle
    if (appointment.isBlockedTime === true) return 'blocked';
    if (appointment.status === 'cancelled') return 'red';

    const ended = appointment.status === 'completed' ||
      (appointment.endTime && new Date(appointment.endTime).getTime() < Date.now());
    const fullyHandled = appointment.isApprovedByTherapist === true &&
      appointment.isTherapistSummarizedTheMeeting === true &&
      appointment.isTherapistUpdatedPaymentStatus === true;

    if (ended && fullyHandled) return 'blue';
    if (ended) return 'orange';

    // Any confirmation declined - show red
    if (appointment.whatsappInitialDeclined === true || appointment.whatsappReminderDeclined === true) return 'red';
    // Both confirmations received - turquoise (uses double check icon in AppointmentCard)
    if (appointment.whatsappInitialConfirmed === true && appointment.whatsappReminderConfirmed === true) return 'turquoise';
    // At least one confirmed (and none declined) - turquoise
    if (appointment.whatsappInitialConfirmed === true || appointment.whatsappReminderConfirmed === true) return 'turquoise';
    // No SMS was requested - treat as confirmed (turquoise)
    if (appointment.initialSmsEnabled === false && appointment.reminderSmsEnabled === false) return 'turquoise';
    // Fallback: no SMS activity at all (handles old data where flags may not be set)
    if (!appointment.whatsappInitialSent && !appointment.whatsappReminderSent && !appointment.initialSmsScheduledAt && !appointment.reminderSmsScheduledAt) return 'turquoise';
    // All are null - waiting for confirmation - yellow
    return 'yellow';
  };

  return (
    <div className="month-view">
      {/* Header */}
      <div className="month-view-header">
        {HEBREW_DAYS_SHORT.map((day, index) => (
          <div key={index} className="weekday-header">
            {day}'
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="month-view-grid">
        {calendarDays.map((dayInfo, index) => {
          const dayKey = getDayKey(dayInfo.date);
          const dayAppointments = appointmentsByDay[dayKey] || [];
          const displayAppointments = dayAppointments.slice(0, 3);
          const moreCount = dayAppointments.length - 3;
          const isAvailable = hasAvailability(dayInfo.date);
          const isBlocked = hasBlockedTime(dayInfo.date);
          const holiday = getHolidayForDate(dayInfo.date);

          return (
            <div
              key={index}
              className={`month-day ${!dayInfo.isCurrentMonth ? 'other-month' : ''} ${isToday(dayInfo.date) ? 'today' : ''} ${isAvailable ? 'has-availability' : ''} ${holiday ? `holiday-${holiday.severity}` : ''}`}
              onClick={() => onDayClick(dayInfo.date)}
              onContextMenu={(e) => handleContextMenu(e, dayInfo.date)}
              data-tooltip={getDayTooltip(dayInfo.date)}
            >
              <div className="day-number-row">
                <span className="day-number">{dayInfo.date.getDate()}</span>
                {holiday && (
                  <span className={`day-holiday-tag ${holiday.severity}`} title={holiday.name}>
                    {holiday.name}
                  </span>
                )}
                {isAvailable && <span className="availability-indicator" title="יש זמנים פנויים"></span>}
                {isBlocked && <span className="blocked-indicator" title="יש זמן חסום"></span>}
              </div>

              <div className="day-appointments">
                {displayAppointments.map((apt) => {
                  const therapistColor = therapistColorMap?.[apt.therapistId]?.color || null;
                  const showTherapist = showTherapistName && apt.therapistName;
                  const seriesSuffix = apt.recurringIndex && apt.recurringTotalCount
                    ? ` (${apt.recurringIndex}/${apt.recurringTotalCount})`
                    : '';
                  const aptTimeStr = new Date(apt.startTime).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
                  const tooltip = showTherapist
                    ? `${apt.therapistName} | ${apt.patientName}${seriesSuffix} - ${aptTimeStr}`
                    : `${apt.patientName}${seriesSuffix} - ${aptTimeStr}`;

                  const colorInfo = resolveAppointmentColor({ appointment: apt, mode: colorMode, treatmentTypeMap });
                  const dotColorClass = colorInfo.kind === 'type'
                    ? `color-mode-type${colorInfo.fallback ? ' type-fallback' : ''}`
                    : `status-${getStatusColor(apt)}`;
                  const dotStyle = colorInfo.kind === 'type'
                    ? { '--apt-type-bg': hexToRgba(colorInfo.hex, 0.2), '--apt-type-text': darkenHex(colorInfo.hex) }
                    : undefined;

                  return (
                    <div
                      key={apt.id}
                      className={`appointment-dot ${dotColorClass}${showTherapist ? ' with-therapist' : ''}`}
                      style={dotStyle}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAppointmentClick(apt);
                      }}
                      onContextMenu={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onAppointmentContextMenu?.(e, apt);
                      }}
                      title={tooltip}
                    >
                      {showTherapist && therapistColor && (
                        <span
                          className="apt-therapist-color-dot"
                          style={{ backgroundColor: therapistColor }}
                          aria-hidden="true"
                        />
                      )}
                      <span className="apt-time">{aptTimeStr}</span>
                      <span className="apt-name">
                        {showTherapist ? `${apt.therapistName} · ${apt.patientName}` : apt.patientName}
                        {seriesSuffix}
                      </span>
                    </div>
                  );
                })}
                {moreCount > 0 && (
                  <div className="more-appointments">
                    +{moreCount} עוד
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Right-click Context Menu */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="calendar-context-menu"
          style={{ position: 'fixed', top: contextMenu.y, left: contextMenu.x }}
        >
          <div className="context-menu-date-header">
            {contextMenu.date.toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
          <button className="context-menu-item" onClick={() => handleMenuAction('create-appointment')}>
            <CalendarPlus size={18} weight="duotone" />
            <span>צור פגישה בתאריך זה</span>
          </button>
          <button className="context-menu-item" onClick={() => handleMenuAction('view-day')}>
            <Eye size={18} weight="duotone" />
            <span>צפה בתצוגה יומית</span>
          </button>
          <div className="context-menu-divider" />
          <button className="context-menu-item" onClick={() => handleMenuAction('add-task')}>
            <ListChecks size={18} weight="duotone" />
            <span>הוסף משימה בתאריך זה</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default MonthView;
